"use client";

import type { IntegrationHeroVisual } from "@marketing/integrations/lib/types";
import { useRevealed } from "@marketing/shared/components/bento/motion";
import { cn } from "@ui/lib";
import { PlusIcon } from "lucide-react";

/**
 * Hero visual for integration pages. Assistant pages get a short
 * conversation with the Spyglasses MCP connected (mirrors McpTerminalTile:
 * same chrome, same staggered reveal); API and automation pages get the
 * request or data that does the work. No third-party logo: the tool is
 * named in text.
 */
export function HeroVisualTile({
	visual,
	className,
}: {
	visual: IntegrationHeroVisual;
	className?: string;
}) {
	return (
		<div className={cn("flex min-w-0 flex-col gap-3", className)}>
			<div className="flex items-center justify-between gap-3">
				<span className="truncate text-xs font-semibold text-muted-foreground">
					{visual.title}
				</span>
				<span className="max-w-[60%] shrink-0 truncate rounded-md border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-bold text-muted-foreground">
					{visual.badge}
				</span>
			</div>
			{visual.kind === "chat" ? (
				<ChatBody
					messages={visual.messages}
					assistantLabel={visual.assistantLabel}
				/>
			) : (
				<pre className="min-w-0 overflow-x-auto rounded-lg border bg-muted/50 p-4 font-mono text-[11px] leading-relaxed text-foreground">
					<code>{visual.code}</code>
				</pre>
			)}
		</div>
	);
}

function ChatBody({
	messages,
	assistantLabel,
}: {
	messages: Extract<IntegrationHeroVisual, { kind: "chat" }>["messages"];
	assistantLabel: string;
}) {
	const revealed = useRevealed();
	const messageClass = cn(
		"motion-safe:transition-all motion-safe:duration-500",
		revealed
			? "motion-safe:translate-y-0 motion-safe:opacity-100"
			: "motion-safe:translate-y-2 motion-safe:opacity-0",
	);
	const messageStyle = (i: number) => ({ transitionDelay: `${i * 260}ms` });

	return (
		<>
			{messages.map((message, i) =>
				message.role === "user" ? (
					<div
						key={message.text}
						className={cn(
							"ml-auto max-w-[85%] rounded-2xl bg-muted px-3.5 py-2 text-xs leading-relaxed text-foreground",
							messageClass,
						)}
						style={messageStyle(i)}
					>
						{message.text}
					</div>
				) : (
					<div
						key={message.text}
						className={cn("max-w-[92%]", messageClass)}
						style={messageStyle(i)}
					>
						{message.tools && message.tools.length > 0 ? (
							<p className="mb-1.5 flex flex-wrap items-center gap-1 text-[11px] italic text-muted-foreground">
								Used
								{message.tools.map((tool) => (
									<code
										key={tool}
										className="break-all rounded border bg-muted px-1 py-px font-mono text-[10px] not-italic"
									>
										{tool}
									</code>
								))}
							</p>
						) : null}
						<p className="text-xs leading-relaxed text-foreground/90">
							{message.text}
						</p>
					</div>
				),
			)}

			<div className="mt-1 flex items-center gap-2 rounded-2xl border bg-background px-3 py-2.5">
				<span
					className="flex size-5 shrink-0 items-center justify-center rounded-full border text-muted-foreground"
					aria-hidden
				>
					<PlusIcon className="size-2.5" />
				</span>
				<span className="flex-1 text-xs text-muted-foreground">
					Write a message…
				</span>
				<span className="shrink-0 text-xs font-semibold text-muted-foreground">
					{assistantLabel}
				</span>
			</div>
		</>
	);
}
